(() => {
  if (customElements.get('ink-cart-artwork-preview')) return;
  const previews = new Map();
  const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'webp', 'svg']);
  const allowedImage = (value, legacy = false) => {
    const url = new URL(value, location.origin);
    if (url.protocol !== 'https:') throw new Error('Invalid image');
    const cloud = url.hostname === 'storage.googleapis.com' || url.hostname.endsWith('.storage.googleapis.com');
    const shopify = (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.myshopify.com')) && url.pathname.includes('/uploads/');
    if (legacy ? !shopify : !cloud) throw new Error('Invalid image');
    return url.href;
  };
  class CartArtworkPreview extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.strings = JSON.parse(this.querySelector('[data-artwork-preview-strings]').textContent);
      this.image = this.querySelector('[data-artwork-image]');
      this.status = this.querySelector('[data-artwork-status]');
      this.dialog = this.querySelector('[data-artwork-dialog]');
      this.key = [this.dataset.artworkId, this.dataset.versionId, this.dataset.legacyUrl].join('|');
      this.querySelectorAll('[data-artwork-open]').forEach(button => button.addEventListener('click', () => this.open()));
      this.querySelector('[data-artwork-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
      this.image.addEventListener('error', () => this.unavailable());
      this.load();
    }
    disconnectedCallback() { if (this.dialog?.open) this.dialog.close(); }
    async json(url, options = {}) {
      const response = await fetch(url, {cache: 'no-store', ...options, signal: AbortSignal.timeout(10000)});
      if (!response.ok) throw new Error('Preview unavailable');
      return response.json();
    }
    async request() {
      if (this.dataset.legacyUrl) {
        const extension = new URL(this.dataset.legacyUrl, location.origin).pathname.split('.').pop().toLowerCase();
        if (!imageExtensions.has(extension)) return {previewAvailable: false, filename: this.dataset.originalName};
        return {previewAvailable: true, url: allowedImage(this.dataset.legacyUrl, true), filename: this.dataset.originalName};
      }
      let sessionId = '';
      try { sessionId = localStorage.getItem('inkelligent_artwork_session_id') || ''; } catch {}
      const endpoint = new URL(this.dataset.endpoint);
      if (endpoint.protocol !== 'https:' || endpoint.hostname !== 'care-api.top') throw new Error('Invalid endpoint');
      if (sessionId) {
        try {
          return await this.json(endpoint.href.replace(/\/+$/, '') + '/v1/artworks/preview', {
            method: 'POST', credentials: 'omit', headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({artworkId: this.dataset.artworkId, versionId: this.dataset.versionId, sessionId}),
          });
        } catch {}
      }
      const account = this.dataset.accountEndpoint;
      if (!account || !account.startsWith('/apps/')) throw new Error('Preview unavailable');
      const kind = this.dataset.hasShape === 'true' ? 'shape-preview' : 'artwork';
      if (kind === 'artwork' && !imageExtensions.has(this.dataset.fileExtension)) return {previewAvailable: false, filename: this.dataset.originalName};
      const result = await this.json(account.replace(/\/+$/, '') + '/artworks/' + encodeURIComponent(this.dataset.artworkId) + '/download/' + kind, {credentials: 'same-origin'});
      return {previewAvailable: true, url: result.url, filename: this.dataset.originalName};
    }
    async load() {
      try {
        let entry = previews.get(this.key);
        if (!entry || entry.expires < Date.now()) {
          entry = {expires: Date.now() + 540000, promise: this.request()};
          previews.set(this.key, entry);
        }
        const result = await entry.promise;
        if (!this.isConnected) return;
        const filename = result.filename || this.dataset.originalName || '';
        this.querySelector('[data-artwork-filename]').textContent = filename;
        this.querySelector('[data-artwork-dialog-filename]').textContent = filename;
        if (!result.previewAvailable) return this.unavailable(this.strings.file_only);
        this.currentUrl = allowedImage(result.url, !!this.dataset.legacyUrl);
        this.image.src = this.currentUrl;
        this.image.hidden = false;
        this.querySelector('[data-artwork-icon]').hidden = true;
        this.status.hidden = true;
        this.querySelectorAll('[data-artwork-open]').forEach(button => { button.disabled = false; button.hidden = false; });
      } catch { previews.delete(this.key); this.unavailable(); }
    }
    unavailable(message = this.strings.unavailable) {
      this.image.hidden = true;
      this.querySelector('[data-artwork-icon]').hidden = false;
      this.status.hidden = false;
      this.status.textContent = message;
      this.querySelectorAll('[data-artwork-open]').forEach(button => { button.disabled = true; });
      this.querySelector('.ink-cart-artwork__view').hidden = true;
    }
    async open() {
      if ((previews.get(this.key)?.expires || 0) < Date.now()) await this.load();
      if (!this.currentUrl || this.image.hidden) return;
      this.querySelector('[data-artwork-large]').src = this.currentUrl;
      this.dialog.showModal();
    }
  }
  customElements.define('ink-cart-artwork-preview', CartArtworkPreview);
})();
